import { useSettings } from '@/framework/settings';
import { MIN_DISCOUNT_PERCENTAGE } from '@/framework/utils/constants';

export function formatPrice({
  amount,
  currencyCode = 'BRL',
  locale = 'pt-BR',
  fractions = 2,
}: {
  amount: number | Intl.StringNumericLiteral;
  currencyCode?: string;
  locale?: string;
  fractions?: number;
}) {
  const formatCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: fractions,
  });

  return formatCurrency.format(amount);
}

export function formatVariantPrice({
  amount,
  baseAmount,
  currencyCode,
  locale,
  fractions = 2,
}: {
  baseAmount: number;
  amount: number;
  currencyCode: string;
  locale: string;
  fractions: number;
}) {
  const hasDiscount = baseAmount > amount;
  const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' });
  const discount = hasDiscount
    ? formatDiscount.format((baseAmount - amount) / baseAmount)
    : null;

  const price = formatPrice({ amount, currencyCode, locale, fractions });
  const basePrice = hasDiscount
    ? formatPrice({ amount: baseAmount, currencyCode, locale, fractions })
    : null;

  return { price, basePrice, discount };
}

export default function usePrice(
  data?: {
    amount: number;
    baseAmount?: number;
    currencyCode?: string;
  } | null,
) {
  const { settings } = useSettings();
  const currency = settings?.currency;
  const currencyOptions = settings?.currencyOptions;

  const {
    amount: amountProp,
    baseAmount,
    currencyCode,
    currencyOptionsFormat,
  } = {
    ...data,
    currencyCode: currency ?? 'BRL',
    currencyOptionsFormat: currencyOptions ?? {
      formation: 'pt-BR',
      fractions: 2,
    },
  };
  const amount = amountProp ?? 0;
  const { formation = 'pt-BR', fractions = 2 } = currencyOptionsFormat!;

  let discount = null;
  let discountPercentage = null;

  if (Number(baseAmount) && Number(amount) < Number(baseAmount)) {
    const discountAmount = Number(baseAmount) - Number(amount);
    discountPercentage = Math.floor(
      (discountAmount / Number(baseAmount)) * 100,
    );
    if (discountPercentage >= MIN_DISCOUNT_PERCENTAGE) {
      discount = `${discountPercentage}% OFF`;
    }
  }

  return {
    price: formatPrice({
      amount: Number(amount),
      currencyCode,
      locale: formation,
      fractions: fractions,
    }),
    basePrice: formatPrice({
      amount: Number(baseAmount),
      currencyCode,
      locale: formation,
      fractions: fractions,
    }),
    discount,
    discountValue: discountPercentage,
  };
}

// Todo: remove usePrice and use calcCurrency
export function calcCurrency({
  settings,
  ...data
}: {
  amount: number;
  baseAmount?: number;
  currencyCode?: string;
  settings?: {
    currency: any;
    currencyOptions: any;
  };
}) {
  const currency = settings?.currency;
  const currencyOptions = settings?.currencyOptions || {
    formation: 'pt-BR',
    fractions: 2,
  };

  const {
    amount: amountProp,
    baseAmount,
    currencyCode,
    currencyOptionsFormat,
  } = {
    ...data,
    currencyCode: currency ?? 'BRL',
    currencyOptionsFormat: currencyOptions ?? {
      formation: 'pt-BR',
      fractions: 2,
    },
  };
  const amount = amountProp ?? 0;
  const { formation = 'pt-BR', fractions = 2 } = currencyOptionsFormat!;

  let discount = null;
  let discountPercentage = null;

  if (Number(baseAmount) && Number(amount) < Number(baseAmount)) {
    const discountAmount = Number(baseAmount) - Number(amount);
    discountPercentage = Math.floor(
      (discountAmount / Number(baseAmount)) * 100,
    );
    if (discountPercentage >= MIN_DISCOUNT_PERCENTAGE) {
      discount = `${discountPercentage}% OFF`;
    }
  }

  return {
    price: formatPrice({
      amount: Number(amount),
      currencyCode,
      locale: formation,
      fractions: fractions,
    }),
    basePrice: formatPrice({
      amount: Number(baseAmount),
      currencyCode,
      locale: formation,
      fractions: fractions,
    }),
    discount,
    discountValue: discountPercentage,
  };
}
